class RoleClaim {
    constructor(scope, organization, role) {
        this.scope = scope;
        this.organization = organization || undefined;
        this.role = role;
    }
    isGlobal() {
        return !this.organization;
    }
    get claimKey() {
        if (this.organization) {
            return `${this.scope}:org:${this.organization}:roles:${this.role}`;
        }
        return `${this.scope}:roles:${this.role}`;
    }
    toHttp() {
        return { scope: this.scope, organization: this.organization, role: this.role };
    }
    static fromHttp(h) {
        return new RoleClaim(h.scope, h.organization, h.role);
    }
    static fromObject(o) {
        return new RoleClaim(o.scope, o.organization, o.role);
    }
    static parse(role, orgToReplace) {
        const scope = role.substring(0, role.indexOf(':'));
        const scopeStripped = role.substring(role.indexOf(':') + 1);
        const roleOrOrganization = scopeStripped.substring(0, scopeStripped.indexOf(':'));
        const roleOrOrganizationStripped = scopeStripped.substring(scopeStripped.indexOf(':') + 1);
        let roleName = null;
        switch (roleOrOrganization) {
            case 'roles':
                roleName = roleOrOrganizationStripped;
                return new RoleClaim(scope, null, roleName);
            case 'org':
                // eslint-disable-next-line no-case-declarations
                const org = roleOrOrganizationStripped.substring(0, roleOrOrganizationStripped.indexOf(':'));
                // eslint-disable-next-line no-case-declarations
                const orgStriped = roleOrOrganizationStripped.substring(roleOrOrganizationStripped.indexOf(':') + 1);
                // eslint-disable-next-line no-case-declarations
                const claimType = orgStriped.substring(0, orgStriped.indexOf(':'));
                roleName = orgStriped.substring(orgStriped.indexOf(':') + 1);
                if (claimType !== 'roles') {
                    throw new Error(`This claim should be a role, not a ${claimType}`);
                }
                return new RoleClaim(scope, orgToReplace || org, roleName);
            default:
                throw new Error(`This claim should be a role, not a ${roleOrOrganization}`);
        }
    }
}

class PropertyClaim {
    constructor(scope, organization, claim, value) {
        this.scope = scope;
        this.organization = organization || undefined;
        this.claim = claim;
        this.value = value;
    }
    isGlobal() {
        return !this.organization;
    }
    get claimKey() {
        if (this.organization) {
            return `${this.scope}:org:${this.organization}:props:${this.claim}`;
        }
        return `${this.scope}:props:${this.claim}`;
    }
    toHttp() {
        return {
            scope: this.scope, organization: this.organization, claim: this.claim, value: this.value,
        };
    }
    static fromObject(o) {
        return new PropertyClaim(o.scope, o.organization, o.claim, o.value);
    }
    static fromHttp(h) {
        return new PropertyClaim(h.scope, h.organization, h.claim, h.value);
    }
    static parse(claim, orgToReplace, value) {
        const scope = claim.substring(0, claim.indexOf(':'));
        const scopeStripped = claim.substring(claim.indexOf(':') + 1);
        const propOrOrganization = scopeStripped.substring(0, scopeStripped.indexOf(':'));
        const propOrOrganizationStripped = scopeStripped.substring(scopeStripped.indexOf(':') + 1);
        let propName = null;
        switch (propOrOrganization) {
            case 'props':
                propName = propOrOrganizationStripped;
                return new PropertyClaim(scope, null, propName, value);
            case 'org':
                // eslint-disable-next-line no-case-declarations
                const org = propOrOrganizationStripped.substring(0, propOrOrganizationStripped.indexOf(':'));
                // eslint-disable-next-line no-case-declarations
                const orgStriped = propOrOrganizationStripped.substring(propOrOrganizationStripped.indexOf(':') + 1);
                // eslint-disable-next-line no-case-declarations
                const claimType = orgStriped.substring(0, orgStriped.indexOf(':'));
                propName = orgStriped.substring(orgStriped.indexOf(':') + 1);
                if (claimType !== 'props') {
                    throw new Error(`This claim should be a prop, not a ${claimType}`);
                }
                return new PropertyClaim(scope, orgToReplace || org, propName, value);
            default:
                throw new Error(`This claim should be a prop, not a ${propOrOrganization}`);
        }
    }
}

function parseClaimsFromIdToken(claims) {
    const result = { properties: [], roles: [] };
    Object.keys(claims).forEach((c) => {
        const scope = c.substring(0, c.indexOf(':'));
        const scopeStriped = c.substring(c.indexOf(':') + 1);
        const propOrRoleOrOrganization = scopeStriped.substring(0, scopeStriped.indexOf(':'));
        const propOrRoleOrOrganizationStriped = scopeStriped.substring(scopeStriped.indexOf(':') + 1);
        // eslint-disable-next-line default-case
        switch (propOrRoleOrOrganization) {
            case 'props':
                const property = propOrRoleOrOrganizationStriped;
                result.properties.push(new PropertyClaim(scope, null, property, claims[c]));
                break;
            case 'roles':
                const role = propOrRoleOrOrganizationStriped;
                result.roles.push(new RoleClaim(scope, null, role));
                break;
            case 'org':
                const org = propOrRoleOrOrganizationStriped.substring(0, propOrRoleOrOrganizationStriped.indexOf(':'));
                const orgStriped = propOrRoleOrOrganizationStriped.substring(propOrRoleOrOrganizationStriped.indexOf(':') + 1);
                const propOrRole = orgStriped.substring(0, orgStriped.indexOf(':'));
                const propOrRoleStriped = orgStriped.substring(orgStriped.indexOf(':') + 1);
                // eslint-disable-next-line default-case
                switch (propOrRole) {
                    case 'props':
                        result.properties.push(new PropertyClaim(scope, org, propOrRoleStriped, claims[c]));
                        break;
                    case 'roles':
                        result.roles.push(new RoleClaim(scope, org, propOrRoleStriped));
                        break;
                }
                break;
        }
    });
    return result;
}

var AuthUserState;
(function (AuthUserState) {
    AuthUserState["NEEDS_REFRESH"] = "needs-refresh";
    AuthUserState["COMPLETE"] = "complete";
})(AuthUserState || (AuthUserState = {}));

/* eslint-disable */
var Claims;
(function (Claims) {
    let Apps;
    (function (Apps) {
        Apps.GLOBAL = 'global';
        Apps.CORE = 'core';
        Apps.BLINDING = 'blinding';
        Apps.FROG_MANAGER = 'frog-manager';
        Apps.FROG_LAB = 'frog-lab';
        Apps.FROG_LINK = 'frog-link';
        Apps.KARABAND = 'karaband';
        Apps.TOC_CHEF = 'toc-chef';
    })(Apps = Claims.Apps || (Claims.Apps = {}));
    let Organization;
    (function (Organization) {
        // Int
        Organization.BLINDING_GLOBAL_PROP_DEMO_PLAYER_CAP = "blinding:props:demo_player_cap";
        // Boolean
        Organization.BLINDING_GLOBAL_PROP_HAS_CURRENT_GAME = "blinding:props:has_current_game";
        // Catalog Id
        Organization.FROG_MANAGER_GLOBAL_PROP_CATALOG_ID = "frog-manager:props:catalog_id";
        // Organization type (one of: partner, internal, provider)
        Organization.FROG_MANAGER_GLOBAL_PROP_ORGANIZATION_TYPE = "frog-manager:props:org_type";
        // Organization main contact email
        Organization.FROG_MANAGER_GLOBAL_PROP_CONTACT_EMAIL = "frog-manager:props:contact_email";
        // Organization main contact name
        Organization.FROG_MANAGER_GLOBAL_PROP_CONTACT_NAME = "frog-manager:props:contact_name";
        // Boolean
        Organization.KARABAND_GLOBAL_PROP_HAS_CURRENT_GAME = "karaband:props:has_current_game";
        // Boolean
        Organization.EVENT_GLOBAL_PROP_HAS_ACTIVE_SESSION = "event:props:has_active_session";
        // Session Id
        Organization.EVENT_GLOBAL_PROP_SESSION_ID = "event:props:session_id";
        // String
        Organization.EVENT_GLOBAL_PROP_USER_NAMING_STRATEGY = "event:props:user_naming_strategy";
        // Boolean
        Organization.EVENT_GLOBAL_PROP_DEMO_ENABLED = "event:props:demo_enabled";
        // Boolean
        Organization.EVENT_GLOBAL_PROP_TEST_ENABLED = "event:props:test_enabled";
        // Boolean
        Organization.EVENT_GLOBAL_PROP_SERVICES_ENABLED = "event:props:services_enabled";
        // String
        Organization.EVENT_GLOBAL_PROP_ACTIVITY_TYPE = "event:props:activity_type";
        // String
        Organization.EVENT_GLOBAL_PROP_ACTIVITY_URI = "event:props:activity_uri";
    })(Organization = Claims.Organization || (Claims.Organization = {}));
    let User;
    (function (User) {
        // Roles
        User.CORE_GLOBAL_ROLE_OWNER = "core:roles:role_owner";
        User.BLINDING_GLOBAL_ROLE_ANONYMOUS = "blinding:roles:role_anonymous";
        User.BLINDING_GLOBAL_ROLE_ADMIN = "blinding:roles:role_admin";
        User.BLINDING_GLOBAL_ROLE_OPERATOR = "blinding:roles:role_operator";
        User.BLINDING_ORG_ROLE_MANAGER = "blinding:org:{org}:roles:role_manager";
        User.BLINDING_ORG_ROLE_LIMITED = "blinding:org:{org}:roles:role_manager_limited";
        User.BLINDING_ORG_ROLE_DEMO = "blinding:org:{org}:roles:role_demo";
        User.FROG_MANAGER_GLOBAL_ROLE_ADMIN = "frog-manager:roles:role_admin";
        User.FROG_MANAGER_GLOBAL_ROLE_BOT = "frog-manager:roles:role_bot";
        User.FROG_MANAGER_ORG_ROLE_MANAGER = "frog-manager:org:{org}:roles:role_manager";
        User.FROG_MANAGER_ORG_ROLE_PARTNER = "frog-manager:org:{org}:roles:role_partner";
        User.FROG_MANAGER_GLOBAL_PROP_DEFAULT_DEMO_ORG = "frog-manager:props:default_demo_organization";
        User.FROG_LAB_GLOBAL_ROLE_ADMIN = "frog-lab:roles:role_admin";
        User.FROG_LAB_ORG_ROLE_MANAGER = "frog-lab:org:{org}:roles:role_manager";
        User.FROG_LAB_GLOBAL_PROP_DEFAULT_ORG = "frog-lab:props:default_organization";
        User.FROG_LINK_GLOBAL_ROLE_ADMIN = "frog-link:roles:role_admin";
        User.KARABAND_GLOBAL_ROLE_ADMIN = "karaband:roles:role_admin";
        User.KARABAND_ORG_ROLE_MANAGER = "karaband:org:{org}:roles:role_manager";
        User.TOC_CHEF_GLOBAL_ROLE_ADMIN = "toc-chef:roles:role_admin";
        User.TOC_CHEF_ORG_ROLE_MANAGER = "toc-chef:org:{org}:roles:role_manager";
        // Claims
        User.GLOBAL_PROP_IAM_UID = "global:props:iam_uid";
    })(User = Claims.User || (Claims.User = {}));
    let FrogEventUser;
    (function (FrogEventUser) {
        FrogEventUser.ORGANIZATION = "event:props:organization";
        FrogEventUser.CREATED_AT = "event:props:created_at";
        FrogEventUser.EVENT_ID = "event:props:event_id";
        FrogEventUser.SESSION_ID = "event:props:session_id";
        FrogEventUser.USER_ID = "event:props:user_id";
        FrogEventUser.AVATAR_URL = "event:props:avatar_url";
        FrogEventUser.DISPLAY_NAME = "event:props:display_name";
        FrogEventUser.SERVICE = "event:props:service";
        // Not present in JWT
        FrogEventUser.NICKNAME = "event:props:nickname";
        FrogEventUser.FIRST_NAME = "event:props:first_name";
        FrogEventUser.LAST_NAME = "event:props:last_name";
        FrogEventUser.EMAIL = "event:props:email";
    })(FrogEventUser = Claims.FrogEventUser || (Claims.FrogEventUser = {}));
})(Claims || (Claims = {}));

class FrogEventUserJwt {
    constructor(authorizationCode, token) {
        this.authorizationCode = authorizationCode;
        this.accessToken = token;
        const payload = JSON.parse(atob(token.split('.')[1]));
        this.userId = payload[Claims.FrogEventUser.USER_ID];
        this.eventId = payload[Claims.FrogEventUser.EVENT_ID];
        this.sessionId = payload[Claims.FrogEventUser.SESSION_ID];
        this.organization = payload[Claims.FrogEventUser.ORGANIZATION];
        this.createdAt = new Date(Date.parse(payload[Claims.FrogEventUser.CREATED_AT]));
        this.service = payload[Claims.FrogEventUser.SERVICE];
        this.avatarUrl = payload[Claims.FrogEventUser.AVATAR_URL];
        this.displayName = payload[Claims.FrogEventUser.DISPLAY_NAME];
    }
    toDto() {
        return {
            accessToken: this.accessToken,
            authorizationCode: this.authorizationCode,
        };
    }
    static fromDto(o) {
        return new FrogEventUserJwt(o.authorizationCode, o.accessToken);
    }
}

class Organization {
    constructor(organization, properties) {
        this.organization = organization;
        this.properties = properties;
    }
    toHttp() {
        return {
            organization: this.organization,
            properties: this.properties?.map((p) => p.toHttp()),
        };
    }
    getBlindingHasCurrentGame() {
        return this.properties?.find((p) => p.claimKey === Claims.Organization.BLINDING_GLOBAL_PROP_HAS_CURRENT_GAME)?.value === true;
    }
    getBlindingDemoPlayerCap() {
        return this.properties?.find((p) => p.claimKey === Claims.Organization.BLINDING_GLOBAL_PROP_DEMO_PLAYER_CAP)?.value || 0;
    }
    getKarabandHasCurrentGame() {
        return this.properties?.find((p) => p.claimKey === Claims.Organization.KARABAND_GLOBAL_PROP_HAS_CURRENT_GAME)?.value === true;
    }
    getFrogManagerOrganizationType() {
        return this.properties?.find((p) => p.claimKey === Claims.Organization.FROG_MANAGER_GLOBAL_PROP_ORGANIZATION_TYPE)?.value;
    }
    getFrogManagerCatalogId() {
        return this.properties?.find((p) => p.claimKey === Claims.Organization.FROG_MANAGER_GLOBAL_PROP_CATALOG_ID)?.value;
    }
    getFrogManagerContactEmail() {
        return this.properties?.find((p) => p.claimKey === Claims.Organization.FROG_MANAGER_GLOBAL_PROP_CONTACT_EMAIL)?.value;
    }
    getFrogManagerContactName() {
        return this.properties?.find((p) => p.claimKey === Claims.Organization.FROG_MANAGER_GLOBAL_PROP_CONTACT_NAME)?.value;
    }
    get eventHasActiveSession() {
        return this.properties?.find((p) => p.claimKey === Claims.Organization.EVENT_GLOBAL_PROP_HAS_ACTIVE_SESSION)?.value === true;
    }
    get eventActiveSessionId() {
        return this.properties?.find((p) => p.claimKey === Claims.Organization.EVENT_GLOBAL_PROP_SESSION_ID)?.value;
    }
    get eventTestEnabled() {
        return this.properties?.find((p) => p.claimKey === Claims.Organization.EVENT_GLOBAL_PROP_TEST_ENABLED)?.value;
    }
    get eventDemoEnabled() {
        return this.properties?.find((p) => p.claimKey === Claims.Organization.EVENT_GLOBAL_PROP_DEMO_ENABLED)?.value;
    }
    get eventServicesEnabled() {
        return this.properties?.find((p) => p.claimKey === Claims.Organization.EVENT_GLOBAL_PROP_SERVICES_ENABLED)?.value;
    }
    get eventUserNamingStrategy() {
        return this.properties?.find((p) => p.claimKey === Claims.Organization.EVENT_GLOBAL_PROP_USER_NAMING_STRATEGY)?.value;
    }
    get eventActivityType() {
        return this.properties?.find((p) => p.claimKey === Claims.Organization.EVENT_GLOBAL_PROP_ACTIVITY_TYPE)?.value;
    }
    get eventActivityUri() {
        return this.properties?.find((p) => p.claimKey === Claims.Organization.EVENT_GLOBAL_PROP_ACTIVITY_URI)?.value;
    }
    static fromHttp(o) {
        if (!o) {
            return;
        }
        // eslint-disable-next-line consistent-return
        return new Organization(o.organization, o.properties?.map((p) => PropertyClaim.fromHttp(p)));
    }
    static fromFirebase(o) {
        if (!o) {
            return;
        }
        const keys = Object.keys(o.properties || {});
        // eslint-disable-next-line consistent-return
        return new Organization(o.organization, keys.map((k) => PropertyClaim.parse(k, o.organization, o.properties?.[k])));
    }
}

var Tenant;
(function (Tenant) {
    Tenant["IAM"] = "iam";
})(Tenant || (Tenant = {}));

class User {
    constructor(id, displayName, avatarUrl, roles, properties) {
        this.id = id;
        this.displayName = displayName;
        this.avatarUrl = avatarUrl;
        this.roles = roles;
        this.properties = properties;
    }
    toHttp() {
        return {
            id: this.id,
            displayName: this.displayName,
            avatarUrl: this.avatarUrl,
            roles: this.roles?.map((r) => r.toHttp()),
            properties: this.properties?.map((p) => p.toHttp()),
        };
    }
    static fromHttp(u) {
        return new User(u.id, u.displayName, u.avatarUrl, u.roles?.map((r) => RoleClaim.fromHttp(r)), u.properties?.map((p) => PropertyClaim.fromHttp(p)));
    }
    getRolesForScope(scope) {
        return this.roles?.filter((r) => r.scope === scope) || [];
    }
    getPropsForScope(scope) {
        return this.properties?.filter((r) => r.scope === scope) || [];
    }
    getRolesForOrg(org) {
        return this.roles?.filter((r) => r.organization === org) || [];
    }
    getPropsForOrg(org) {
        return this.properties?.filter((r) => r.organization === org) || [];
    }
    /**
       * This will return true whether the role is global or for a specific org
       * @param roles
       */
    hasAnyRole(roles) {
        return this.roles?.filter((r) => roles.filter((r2) => r.scope === r2.scope && r.role === r2.role && (!r.organization || r.organization === r2.organization))
            .length > 0).length > 0;
    }
    /**
       * Strict means that a global role won't be considered as a valid role.
       * @param roles
       */
    hasAnyRoleStrict(roles) {
        return this.roles?.filter((r) => roles.filter((r2) => r.scope === r2.scope && r.role === r2.role && (r.organization === r2.organization))
            .length > 0).length > 0;
    }
    hasRole(scope, role, organization) {
        return this.roles?.filter((r) => r.scope === scope && r.role === role && (!r.organization || r.organization === organization)).length > 0;
    }
    hasGlobalRole(scope, role) {
        return this.roles?.filter((r) => r.scope === scope && r.role === role && !r.organization).length > 0;
    }
    hasRoleForOrg(scope, role, organization) {
        return this.roles?.filter((r) => r.scope === scope && r.role === role && r.organization === organization).length > 0;
    }
}
class AuthUser extends User {
    constructor(id, name, avatarUrl, roles, properties, anonymous, authenticated, state, tenant) {
        super(id || undefined, name, avatarUrl || undefined, roles, properties);
        this.anonymous = anonymous;
        this.authenticated = authenticated || false;
        this.state = state || AuthUserState.NEEDS_REFRESH;
        this.tenant = tenant || Tenant.IAM;
    }
    static anonymousUnauthenticated() {
        return new AuthUser(null, 'Anonymous', null, [], [], true, false);
    }
    static fromObject(user) {
        if (!user) {
            return null;
        }
        return new AuthUser(user.id, user.displayName, user.avatarUrl, user.roles?.map((r) => RoleClaim.fromObject(r)), user.properties?.map((p) => PropertyClaim.fromObject(p)), user.anonymous, user.authenticated, user.state, user.tenant);
    }
    static fromIam(firebaseIamUser, tokenClaims) {
        const claims = parseClaimsFromIdToken(tokenClaims);
        return new AuthUser(firebaseIamUser.uid, (firebaseIamUser.displayName || firebaseIamUser.email), firebaseIamUser.photoUrl, claims.roles, claims.properties, false, true, AuthUserState.COMPLETE, Tenant.IAM);
    }
}

class Build {
    constructor(artifact, group, name, version, time) {
        this.artifact = artifact;
        this.group = group;
        this.name = name;
        this.version = version;
        this.time = time;
    }
}

/**
 * Generated bundle index. Do not edit.
 */

export { AuthUser, AuthUserState, Build, Claims, FrogEventUserJwt, Organization, PropertyClaim, RoleClaim, Tenant, User, parseClaimsFromIdToken };
